import { useLocation, Link } from 'react-router-dom'
import { useState } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import CloseIcon from '@mui/icons-material/Close'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import kumesaLogo from '../../styles/images/kumesa-logo.png'

type Props = {
  isOpen: boolean,
  onClose: () => void,
}

type MenuChild = { label: string; path: string }

type MenuItem = {
  label: string
  icon?: React.ReactNode
  path?: string
  adminOnly?: boolean
  children?: MenuChild[]
}

const SideNav = ({ isOpen, onClose }: Props) => {
  const [clicked, setClicked] = useState(-1)

  const role = sessionStorage.getItem('role') || ''
  const tenantName = sessionStorage.getItem('tenantName') || ''
  const isPlatformAdmin = role === 'PLATFORM_ADMIN'
  const isTenantAdmin = role === 'TENANT_ADMIN'
  const headerLabel = isPlatformAdmin ? 'Kumesa' : (tenantName || 'Kumesa')

  // the same icons as the Kumesa app's tabs and "More" screen
  const iconProps = { fontSize: 'small' as const }
  const platformMenu: MenuItem[] = [
    { label: 'Laundries', icon: <StorefrontOutlinedIcon {...iconProps} />, path: '/admin/tenants' },
  ]

  const tenantMenu: MenuItem[] = [
    { label: 'Reports', icon: <InsightsOutlinedIcon {...iconProps} />, path: '/reports', adminOnly: true },
    { label: 'Orders', icon: <ReceiptLongOutlinedIcon {...iconProps} />, path: '/orders' },
    { label: 'Customers', icon: <PeopleOutlineIcon {...iconProps} />, path: '/customers' },
    { label: 'Expenses', icon: <ShoppingBagOutlinedIcon {...iconProps} />, path: '/expenses' },
    {
      label: 'Subscriptions',
      icon: <AutorenewIcon {...iconProps} />,
      children: [
        { label: 'Members', path: '/subscriptions' },
        { label: 'Orders', path: '/subscription-orders' },
      ],
    },
    { label: 'Employees', icon: <BadgeOutlinedIcon {...iconProps} />, path: '/employees', adminOnly: true },
    { label: 'Salaries', icon: <AccountBalanceWalletOutlinedIcon {...iconProps} />, path: '/salaries', adminOnly: true },
    { label: 'Users', icon: <ManageAccountsOutlinedIcon {...iconProps} />, path: '/users', adminOnly: true },
    {
      label: 'Settings',
      icon: <SettingsOutlinedIcon {...iconProps} />,
      children: [
        { label: 'Item categories', path: '/item-categories' },
        { label: 'Price per kg', path: '/price-per-kg' },
      ],
    },
  ]

  const menuItems: MenuItem[] = (isPlatformAdmin ? platformMenu : tenantMenu)
    .filter((item) => !item.adminOnly || isTenantAdmin || isPlatformAdmin)

  const location = useLocation()
  const pathname = location.pathname

  const handleToggle = (id: number) => {
    setClicked(clicked === id ? -1 : id)
  }

  const handleNavigate = () => {
    onClose()
  }

  return (
    <>
      {isOpen && (
        <button
          aria-label="Close navigation"
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div className={`fixed inset-y-0 left-0 w-64 bg-[var(--surface)] border-r border-[var(--border)] flex flex-col z-50 transform transition-transform duration-300 ease-out lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="px-5 py-5 border-b border-[var(--border)] shrink-0 flex items-center justify-between">
          <span className='font-bold text-base flex items-center gap-2.5 min-w-0'>
            <img src={kumesaLogo} alt='' width={30} height={30} className='rounded-[9px] shrink-0' />
            <span className='truncate'>{headerLabel}</span>
          </span>
          <button aria-label="Close menu" className="lg:hidden text-[var(--text-muted)]" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">

          {menuItems.map((menuItem, id) => {
            if (!menuItem.children) {
              const path = menuItem.path || '#'
              return (
                <Link
                  key={id}
                  to={path}
                  onClick={handleNavigate}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition
                  ${pathname.includes(path)
                      ? 'bg-[var(--primary-soft)] text-[var(--primary-dark)]'
                      : 'text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'}`}
                >
                  {menuItem.icon}
                  <span>{menuItem.label}</span>
                </Link>
              )
            }

            return (
              <div key={id}>
                <button
                  onClick={() => handleToggle(id)}
                  className="w-full flex justify-between items-center px-4 py-3 rounded-xl text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                >
                  <span className="flex items-center gap-3">{menuItem.icon}<span>{menuItem.label}</span></span>
                  <KeyboardArrowDownOutlinedIcon className={`transition ${clicked === id && 'rotate-180'}`} />
                </button>

                {clicked === id && (
                  <div className="pl-4 mt-2 space-y-1">
                    {menuItem.children.map((el, childIndex) => (
                      <Link
                        key={childIndex}
                        to={el.path}
                        onClick={handleNavigate}
                        className={`block px-4 py-2 rounded-xl text-sm transition
                        ${pathname.includes(el.path)
                            ? 'bg-[var(--primary-soft)] text-[var(--primary-dark)]'
                            : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)]'}`}
                      >
                        {el.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default SideNav
